import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, X, Warehouse, PackageCheck, Ban, Loader2, Download, AlertTriangle } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime, formatCurrency } from '../lib/utils'
import { useAuth } from '../context/AuthContext'

// Stock counts (Phase 12.2). Start a count, walk the shelves, type
// the physical qty per SKU, Post writes count_correction stock_movements
// and applies the variance to products.stock.

// Admin /categories aliases `name_en` → `name` in the response; match
// that shape (learned the same lesson on the PO product picker).
interface Category { id: string; slug: string; name: string; name_ar: string; active: number }
interface StockCount {
  id: string
  count_number: string
  store_id: string
  category_id: string | null
  category_name: string | null
  status: 'open' | 'posted' | 'cancelled'
  notes: string | null
  opened_at: string
  posted_at: string | null
  cancelled_at: string | null
  variance_qty_total: number
  variance_value_fils: number
  created_at: string
}

interface StockCountItem {
  id: string
  stock_count_id: string
  product_id: string
  product_name: string | null
  product_sku: string | null
  system_qty_at_open: number
  unit_cost_fils: number
  typed_qty: number | null
  variance_qty: number
  variance_value_fils: number
  notes: string | null
}

const STATUS_LABEL: Record<StockCount['status'], string> = {
  open: 'Open', posted: 'Posted', cancelled: 'Cancelled',
}
const STATUS_COLOR: Record<StockCount['status'], string> = {
  open:      'bg-blue-500/15 text-blue-400',
  posted:    'bg-green-500/15 text-green-400',
  cancelled: 'bg-red-500/15 text-red-400',
}

export default function StockCounts() {
  const { can } = useAuth()
  const canManage = can(['super_admin', 'operations'])
  const [filters, setFilters] = useState<{ status: string }>({ status: '' })
  const [createOpen, setCreateOpen] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)

  const query = useMemo(() => {
    const q = new URLSearchParams()
    if (filters.status) q.set('status', filters.status)
    return q.toString()
  }, [filters])

  const { data, isLoading } = useQuery<{ stock_counts: StockCount[]; total: number }>({
    queryKey: ['stock-counts', query],
    queryFn: () => api.get(`/stock-counts${query ? `?${query}` : ''}`).then(r => r.data),
  })

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Warehouse size={20} className="text-[#c41e3a]" />
            Stock Counts
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Physical inventory reconciliation. Start a count (whole store or one category), walk the shelves, type the counted qty back in, and Post writes <code>count_correction</code> movements to the ledger + applies the variance to <code>products.stock</code>. Variance value shows up in the Shrinkage tile on Sales.
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830]"
          >
            <Plus size={16} />
            Start count
          </button>
        )}
      </div>

      <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Status">
          <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className={selectCls}>
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="posted">Posted</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </Field>
      </div>

      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
        ) : (data?.stock_counts ?? []).length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            No stock counts yet. Start one to reconcile the shelves.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-white/5 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Count #</th>
                <th className="text-left px-5 py-3 font-medium">Scope</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
                <th className="text-right px-5 py-3 font-medium">Variance qty</th>
                <th className="text-right px-5 py-3 font-medium">Variance value</th>
                <th className="text-left px-5 py-3 font-medium">Opened</th>
                <th className="text-left px-5 py-3 font-medium">Posted</th>
              </tr>
            </thead>
            <tbody>
              {(data?.stock_counts ?? []).map(sc => (
                <tr key={sc.id}
                    className="border-t border-white/10 hover:bg-white/5 cursor-pointer"
                    onClick={() => setOpenId(sc.id)}>
                  <td className="px-5 py-3 text-sm text-white font-mono font-semibold">{sc.count_number}</td>
                  <td className="px-5 py-3 text-sm text-gray-300">{sc.category_name ?? <span className="text-gray-500">Whole store</span>}</td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLOR[sc.status]}`}>{STATUS_LABEL[sc.status]}</span>
                  </td>
                  <td className={`px-5 py-3 text-sm text-right font-mono ${sc.variance_qty_total < 0 ? 'text-red-400' : sc.variance_qty_total > 0 ? 'text-green-400' : 'text-gray-400'}`}>
                    {sc.status === 'posted' ? sc.variance_qty_total : '—'}
                  </td>
                  <td className={`px-5 py-3 text-sm text-right font-mono ${sc.variance_value_fils < 0 ? 'text-red-400' : sc.variance_value_fils > 0 ? 'text-green-400' : 'text-gray-400'}`}>
                    {sc.status === 'posted' ? formatCurrency(sc.variance_value_fils) : '—'}
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-500">{formatDateTime(sc.opened_at)}</td>
                  <td className="px-5 py-3 text-sm text-gray-500">{sc.posted_at ? formatDateTime(sc.posted_at) : <span className="text-gray-600">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {createOpen && (
        <StartCountModal
          onClose={() => setCreateOpen(false)}
          onStarted={(id) => { setCreateOpen(false); setOpenId(id) }}
        />
      )}

      {openId && (
        <CountDrawer
          countId={openId}
          canManage={canManage}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  )
}

const inputCls = 'w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c41e3a]'
const selectCls = inputCls

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs text-gray-500 uppercase tracking-wide">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-[11px] text-gray-600 mt-1">{hint}</p>}
    </div>
  )
}

// ── Start count ──────────────────────────────────────────────────────
function StartCountModal({ onClose, onStarted }: { onClose: () => void; onStarted: (id: string) => void }) {
  const [scope, setScope] = useState<'store' | 'category'>('store')
  const [categoryId, setCategoryId] = useState<string>('')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: catsData } = useQuery<{ categories: Category[] }>({
    queryKey: ['categories'],
    queryFn: () => api.get('/categories').then(r => r.data),
  })
  const categories = catsData?.categories ?? []

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (scope === 'category' && !categoryId) { setError('Pick a category'); return }
    setSubmitting(true)
    try {
      const res = await api.post('/stock-counts', {
        category_id: scope === 'category' ? categoryId : null,
        notes: notes.trim() || null,
      })
      onStarted(res.data.id as string)
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message ?? "Couldn't start count.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={e => e.stopPropagation()}>
      <form onSubmit={submit} className="w-full max-w-md bg-[#1a1a1a] border border-white/10 rounded-xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-base font-semibold">Start stock count</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>

        <Field label="Scope">
          <div className="flex gap-2">
            <button type="button" onClick={() => setScope('store')} className={`flex-1 text-sm py-2 rounded-lg border ${scope === 'store' ? 'border-[#c41e3a] bg-[#c41e3a]/15 text-white' : 'border-[#3a3a3a] text-gray-400'}`}>Whole store</button>
            <button type="button" onClick={() => setScope('category')} className={`flex-1 text-sm py-2 rounded-lg border ${scope === 'category' ? 'border-[#c41e3a] bg-[#c41e3a]/15 text-white' : 'border-[#3a3a3a] text-gray-400'}`}>One category</button>
          </div>
        </Field>

        {scope === 'category' && (
          <Field label="Category">
            <select value={categoryId} onChange={e => setCategoryId(e.target.value)} className={selectCls}>
              <option value="">Pick a category…</option>
              {categories
                .filter(c => c.active !== 0)
                .map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
        )}

        <Field label="Notes (optional)">
          <textarea value={notes} onChange={e => setNotes(e.target.value)} className={`${inputCls} min-h-[60px]`} placeholder="Reason for the count, who's counting, etc." />
        </Field>

        {error && <p className="text-xs text-red-400">{error}</p>}

        <div className="flex items-center gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 text-sm py-2 rounded-lg border border-[#3a3a3a] text-gray-400 hover:bg-[#242424]">Cancel</button>
          <button type="submit" disabled={submitting} className="flex-1 text-sm py-2 rounded-lg bg-[#c41e3a] text-white font-medium hover:bg-[#a01830] disabled:opacity-50">
            {submitting ? 'Starting…' : 'Start count'}
          </button>
        </div>
      </form>
    </div>
  )
}

// ── Detail drawer ─────────────────────────────────────────────────────
function CountDrawer({ countId, canManage, onClose }: { countId: string; canManage: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const [typed, setTyped] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data, refetch, isLoading } = useQuery<{ stock_count: StockCount; items: StockCountItem[] }>({
    queryKey: ['stock-count', countId],
    queryFn: async () => {
      const r = await api.get(`/stock-counts/${countId}`)
      return r.data
    },
  })
  const sc = data?.stock_count
  const items = data?.items ?? []

  // Seed the typed state from server data on every refetch.
  useMemo(() => {
    const seed: Record<string, string> = {}
    for (const it of items) seed[it.id] = it.typed_qty == null ? '' : String(it.typed_qty)
    setTyped(seed)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, sc?.status])

  const varianceRows = useMemo(() => {
    if (!items.length) return []
    return items.map(it => {
      const raw = typed[it.id]
      const qty = raw === undefined || raw === '' ? null : parseInt(raw, 10)
      const varianceQty = qty === null || Number.isNaN(qty) ? null : qty - it.system_qty_at_open
      const varianceVal = varianceQty === null ? null : varianceQty * it.unit_cost_fils
      return { ...it, entered: qty, varianceQty, varianceVal }
    })
  }, [items, typed])

  const totals = useMemo(() => {
    let qty = 0, val = 0, counted = 0
    for (const r of varianceRows) {
      if (r.varianceQty !== null) {
        qty += r.varianceQty
        val += r.varianceVal ?? 0
        counted += 1
      }
    }
    return { qty, val, counted }
  }, [varianceRows])

  async function saveTyped() {
    setBusy('save')
    setError(null)
    try {
      const lines = items.map(it => ({
        item_id: it.id,
        typed_qty: (() => {
          const raw = typed[it.id]
          if (raw === undefined || raw === '') return null
          const n = parseInt(raw, 10)
          return Number.isFinite(n) && n >= 0 ? n : null
        })(),
      }))
      await api.patch(`/stock-counts/${countId}/items`, { lines })
      await refetch()
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message ?? "Couldn't save.")
    } finally {
      setBusy(null)
    }
  }

  async function runAction(action: 'post' | 'cancel', confirmMsg: string) {
    if (!confirm(confirmMsg)) return
    setBusy(action)
    setError(null)
    try {
      // Always flush the currently-typed values before Post so the
      // server has the final numbers.
      if (action === 'post') await saveTyped()
      await api.post(`/stock-counts/${countId}/${action}`, {})
      await refetch()
      qc.invalidateQueries({ queryKey: ['stock-counts'] })
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message ?? `Couldn't ${action}.`)
    } finally {
      setBusy(null)
    }
  }

  function exportCsv() {
    if (!items.length || !sc) return
    const rows = [
      ['SKU', 'Product', 'System qty', 'Typed qty', 'Variance qty', 'Unit cost (JD)', 'Variance value (JD)'],
      ...varianceRows.map(r => [
        r.product_sku ?? '',
        r.product_name ?? '',
        r.system_qty_at_open,
        r.entered ?? '',
        r.varianceQty ?? '',
        (r.unit_cost_fils / 1000).toFixed(3),
        r.varianceVal !== null ? (r.varianceVal / 1000).toFixed(3) : '',
      ]),
    ]
    const csv = rows.map(r => r.map(cell => {
      const s = String(cell)
      return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s
    }).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${sc.count_number}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/60" onClick={onClose}>
      <div className="w-full max-w-4xl h-full bg-[#0f0f0f] border-l border-white/10 overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Stock Count</div>
              <div className="text-2xl font-mono font-bold text-white mt-1">{sc?.count_number ?? '…'}</div>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={22} /></button>
          </div>

          {/* Warn if any items in scope have no cost basis. Variance
              QUANTITIES are still accurate, but variance VALUES read zero
              → the Shrinkage tile on Sales will misleadingly show 0.
              Non-blocking; the operator can still Post. */}
          {sc?.status === 'open' && items.some(i => i.unit_cost_fils === 0) && (
            <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle size={18} className="text-amber-400 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-200">
                <span className="font-semibold">{items.filter(i => i.unit_cost_fils === 0).length}</span> of {items.length} items in scope have no cost basis (<code className="text-xs">products.cost_fils = 0</code>).
                Variance quantities will be accurate, but variance <em>values</em> for those items will read zero.
                Receive stock via a Purchase Order first to unlock cost-based variance reporting on future counts.
              </div>
            </div>
          )}

          {sc && (
            <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
              <MiniStat label="Scope" value={sc.category_name ?? 'Whole store'} />
              <MiniStat label="Status" value={
                <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLOR[sc.status]}`}>{STATUS_LABEL[sc.status]}</span>
              } />
              <MiniStat label="Items counted" value={sc.status === 'posted' ? items.filter(i => i.typed_qty !== null).length : `${totals.counted} / ${items.length}`} />
              {sc.status === 'posted' && <>
                <MiniStat label="Total variance qty" value={<span className={sc.variance_qty_total < 0 ? 'text-red-400' : sc.variance_qty_total > 0 ? 'text-green-400' : ''}>{sc.variance_qty_total}</span>} />
                <MiniStat label="Total variance value" value={<span className={`font-mono ${sc.variance_value_fils < 0 ? 'text-red-400' : sc.variance_value_fils > 0 ? 'text-green-400' : ''}`}>{formatCurrency(sc.variance_value_fils)}</span>} />
              </>}
              {sc.status === 'open' && <>
                <MiniStat label="Running variance qty" value={<span className={totals.qty < 0 ? 'text-red-400' : totals.qty > 0 ? 'text-green-400' : ''}>{totals.qty}</span>} />
                <MiniStat label="Running variance value" value={<span className={`font-mono ${totals.val < 0 ? 'text-red-400' : totals.val > 0 ? 'text-green-400' : ''}`}>{formatCurrency(totals.val)}</span>} />
              </>}
              {sc.notes && <MiniStat label="Notes" value={<span className="whitespace-pre-line">{sc.notes}</span>} className="md:col-span-3" />}
            </div>
          )}

          {/* Items */}
          <div className="bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="text-sm font-semibold text-white">Line items ({items.length})</div>
              <button onClick={exportCsv} className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-md border border-[#3a3a3a] text-gray-300 hover:bg-white/5">
                <Download size={12} /> Export CSV
              </button>
            </div>
            {isLoading ? (
              <div className="p-6 text-center text-sm text-gray-500">Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">No items in this count.</div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-white/5 text-[11px] uppercase tracking-wide text-gray-500 sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-2">SKU</th>
                      <th className="text-left px-4 py-2">Product</th>
                      <th className="text-right px-4 py-2">System</th>
                      <th className="text-right px-4 py-2 w-32">Counted</th>
                      <th className="text-right px-4 py-2">Variance</th>
                      <th className="text-right px-4 py-2">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {varianceRows.map(r => (
                      <tr key={r.id} className="border-t border-white/10">
                        <td className="px-4 py-2 text-xs text-gray-400 font-mono">{r.product_sku ?? '—'}</td>
                        <td className="px-4 py-2 text-sm text-white truncate max-w-[280px]">{r.product_name ?? '—'}</td>
                        <td className="px-4 py-2 text-sm text-gray-300 text-right font-mono">{r.system_qty_at_open}</td>
                        <td className="px-4 py-2">
                          {sc?.status === 'open' && canManage ? (
                            <input
                              type="number"
                              min={0}
                              value={typed[r.id] ?? ''}
                              onChange={e => setTyped(t => ({ ...t, [r.id]: e.target.value }))}
                              className={`${inputCls} text-right font-mono`}
                              placeholder="—"
                            />
                          ) : (
                            <div className="text-right font-mono text-sm text-white">{r.typed_qty ?? '—'}</div>
                          )}
                        </td>
                        <td className={`px-4 py-2 text-sm text-right font-mono ${(r.varianceQty ?? 0) < 0 ? 'text-red-400' : (r.varianceQty ?? 0) > 0 ? 'text-green-400' : 'text-gray-500'}`}>
                          {r.varianceQty ?? '—'}
                        </td>
                        <td className={`px-4 py-2 text-sm text-right font-mono ${(r.varianceVal ?? 0) < 0 ? 'text-red-400' : (r.varianceVal ?? 0) > 0 ? 'text-green-400' : 'text-gray-500'}`}>
                          {r.varianceVal !== null ? formatCurrency(r.varianceVal) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {error && <div className="text-sm text-red-400">{error}</div>}

          {sc?.status === 'open' && canManage && (
            <div className="flex flex-wrap gap-2">
              <button disabled={busy !== null} onClick={saveTyped}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/5 border border-[#3a3a3a] text-gray-200 text-sm font-semibold hover:bg-white/10 disabled:opacity-40">
                {busy === 'save' ? <Loader2 size={14} className="animate-spin" /> : null}
                Save progress
              </button>
              <button disabled={busy !== null} onClick={() => runAction('post', `Post ${sc.count_number}? This applies the variance to stock and cannot be undone.`)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-40">
                {busy === 'post' ? <Loader2 size={14} className="animate-spin" /> : <PackageCheck size={14} />}
                Post count
              </button>
              <button disabled={busy !== null} onClick={() => runAction('cancel', `Cancel ${sc.count_number}? Typed quantities will be lost.`)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-500/15 border border-red-500/40 text-red-300 text-sm font-semibold hover:bg-red-500/25 disabled:opacity-40">
                {busy === 'cancel' ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                Cancel count
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function MiniStat({ label, value, className = '' }: { label: string; value: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-sm text-white mt-0.5">{value}</div>
    </div>
  )
}
